import React, { useMemo } from "react";

import { IgTextField, IgTypography } from "@intelligems/ig-theme";
import {
  OrgShopIdDto,
  StripeApiGetAllProductsResponseBodyType,
  StripeApiProductDto,
} from "@intelligems/ig-types";
import { Grid, Skeleton } from "@mui/material";
import { useStripeApi } from "@src/api/stripe/stripe-api";
import { VirtualAutoComplete } from "@src/components/VirtualAutoComplete/VirtualAutoComplete";
import { filterByNameOrId } from "@src/features/admin/components/Fields/filter-by-name-or-id";

const Formatter = (data: StripeApiProductDto) => {
  return (
    <Grid container columnSpacing={1} alignItems={"center"}>
      <Grid item>
        <IgTypography>{data.name}</IgTypography>
      </Grid>
    </Grid>
  );
};

interface ChooseProductFieldProps {
  orgs: OrgShopIdDto[];
  filteredIds: string[];
  product: StripeApiProductDto | null;
  onChange: (value: StripeApiProductDto) => void;
}

export const ChooseProductField = ({
  filteredIds,
  orgs,
  product,
  onChange,
}: ChooseProductFieldProps) => {
  const subscriptions = useStripeApi<StripeApiGetAllProductsResponseBodyType>({
    action: "getAllProducts",
    orgs,
  });

  const data = useMemo(() => {
    if (!subscriptions.isSuccess) {
      return [];
    }
    if (filteredIds) {
      return subscriptions.data.products.filter(
        (p) => !filteredIds.includes(p.id)
      );
    }
    return subscriptions.data.products;
  }, [subscriptions.isSuccess, subscriptions.data, filteredIds]);

  if (!subscriptions.isSuccess) {
    return <Skeleton variant="rectangular" height={40} />;
  }

  return (
    <VirtualAutoComplete
      sx={{
        "& .MuiInputBase-root": {
          height: "40px",
        },
      }}
      formatter={Formatter}
      onChange={(e, newValue: StripeApiProductDto) => {
        onChange(newValue);
      }}
      componentsProps={{
        paper: {
          sx: {
            "&.MuiPaper-root": {
              width: "275px",
            },
          },
        },
        popper: { sx: { zIndex: 1800 } },
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      options={data}
      getOptionLabel={(option: StripeApiProductDto) => option.name}
      renderInput={(params) => <IgTextField {...params} variant={"standard"} />}
      value={product}
      filterOptions={filterByNameOrId}
    />
  );
};
